import { useState, useCallback } from 'react';
import { certificateApi } from 'configs/api';

interface Certificate {
  id: string;
  userID: string;
  courseID: string;
  enrollmentID: string;
  certificateNumber: string;
  issuedAt: string;
  pdfUrl?: string;
  imageUrl?: string;
  createdAt?: string;
}

export const useCertificate = () => {
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const getCertificateByEnrollment = useCallback(async (enrollmentId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await certificateApi.get(`/api/certificates/enrollment/${enrollmentId}`);
      if (response.data?.status && response.data?.data) {
        return response.data.data as Certificate;
      }
      return null;
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to fetch certificate';
      setError(errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const getUserCertificates = useCallback(async (userId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await certificateApi.get(`/api/certificates/user/${userId}`);
      if (response.data?.status && Array.isArray(response.data?.data)) {
        setCertificates(response.data.data);
        return response.data.data as Certificate[];
      }
      return [];
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to fetch certificates';
      setError(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getCourseCertificates = useCallback(async (courseId: string) => {
    setLoading(true);
    setError('');
    try {
      const response = await certificateApi.get(`/api/certificates/course/${courseId}`);
      if (response.data?.status && Array.isArray(response.data?.data)) {
        return response.data.data as Certificate[];
      }
      return [];
    } catch (err: any) {
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to fetch certificates';
      setError(errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const issueCertificate = useCallback(
    async (userId: string, courseId: string, enrollmentId: string, certificateNumber: string) => {
      setLoading(true);
      setError('');
      try {
        const response = await certificateApi.post('/api/certificates', {
          userID: userId,
          courseID: courseId,
          enrollmentID: enrollmentId,
          certificateNumber: certificateNumber,
          issuedAt: new Date().toISOString(),
        });
        if (response.data?.status && response.data?.data) {
          return response.data.data as Certificate;
        }
        throw new Error('Failed to issue certificate');
      } catch (err: any) {
        const errorMsg = err?.response?.data?.message || err?.message || 'Failed to issue certificate';
        setError(errorMsg);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    certificates,
    loading,
    error,
    getCertificateByEnrollment,
    getUserCertificates,
    getCourseCertificates,
    issueCertificate,
  };
};
